import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useParams
} from "react-router-dom";
import Preloader from "../src/components/Pre";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import PageBubbles from "./components/PageBubbles";
import "./style.css";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { DEFAULT_FONT_ID, getFontPreset } from "./fonts";

// Route pages are code-split so each one loads only when its route is visited,
// shrinking the initial JS bundle.
const Home = lazy(() => import("./components/Home/Home"));
const Projects = lazy(() => import("./components/Projects/Projects"));
const Blogs = lazy(() => import("./components/Blogs/Blogs"));
const BlogDetail = lazy(() => import("./components/Blogs/BlogDetail"));
const Frames = lazy(() => import("./components/Frames/Frames"));
const Arena = lazy(() => import("./components/Arena/Arena"));

function LegacyBlogRedirect() {
  const { id } = useParams();
  return <Navigate to={`/journal/${id}`} replace />;
}

function App() {
  const [load, upadateLoad] = useState(true);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light"
  );
  const [fontId, setFontId] = useState(
    () => localStorage.getItem("font") || DEFAULT_FONT_ID
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      upadateLoad(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("light-mode", theme === "light");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const preset = getFontPreset(fontId);
    const root = document.documentElement;
    root.style.setProperty("--font-body", preset.body);
    root.style.setProperty("--font-display", preset.display);
    root.dataset.font = preset.id;
    localStorage.setItem("font", preset.id);
  }, [fontId]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  return (
    <Router>
      <Preloader load={load} />
      <div className="App" id={load ? "no-scroll" : "scroll"}>
        <Navbar
          theme={theme}
          toggleTheme={toggleTheme}
          fontId={fontId}
          setFontId={setFontId}
        />
        <ScrollToTop />
        <PageBubbles />
        <div className="site-shell">
          <Sidebar />
          <div className="site-main">
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/lab" element={<Projects />} />
                <Route path="/journal" element={<Blogs />} />
                <Route path="/journal/:id" element={<BlogDetail />} />
                <Route path="/frames" element={<Frames />} />
                <Route path="/arena" element={<Arena />} />

                {/* Redirects from old routes */}
                <Route path="/mindscape" element={<Navigate to="/" />} />
                <Route path="/mind" element={<Navigate to="/" />} />
                <Route path="/systems" element={<Navigate to="/lab" />} />
                <Route path="/signals" element={<Navigate to="/journal" />} />
                <Route path="/signals/:id" element={<LegacyBlogRedirect />} />
                <Route path="/about" element={<Navigate to="/" />} />
                <Route path="/project" element={<Navigate to="/lab" />} />
                <Route path="/blogs" element={<Navigate to="/journal" />} />
                <Route path="/blogs/:id" element={<LegacyBlogRedirect />} />

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
            <Footer />
          </div>
        </div>
      </div>
    </Router>
  );
}

export default App;
